import { Modal, Input, Select, Switch } from "antd";
import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import HybridInput from "./Form/HybridInput";
import DecimalInputNumber from "../common/DecimalInputNumber";

type FieldType = "text" | "number" | "textarea" | "select" | "hybrid" | "switch";

type SelectOption = { label: string; value: string | number };

type InlineEditModalProps = {
  open: boolean;
  fieldLabel: string;
  fieldType: FieldType;
  value: any;
  options?: SelectOption[];
  optionsLoading?: boolean;
  saving?: boolean;
  onSave: (newValue: any) => void;
  onClose: () => void;
};

export default function InlineEditModal({
  open,
  fieldLabel,
  fieldType,
  value,
  options = [],
  optionsLoading = false,
  saving = false,
  onSave,
  onClose,
}: InlineEditModalProps) {
  const [localValue, setLocalValue] = useState<any>(value);

  useEffect(() => {
    if (open) setLocalValue(value);
  }, [open, value]);

  const handleOk = useCallback(() => {
    onSave(localValue);
  }, [localValue, onSave]);

  const renderInput = () => {
    switch (fieldType) {
      case "text":
        return (
          <Input
            size="large"
            className="custom-input"
            value={localValue ?? ""}
            onChange={(e) => setLocalValue(e.target.value)}
            autoFocus
          />
        );
      case "number":
        return (
          <DecimalInputNumber
            size="large"
            className="custom-input"
            style={{ width: "100%" }}
            value={localValue ?? null}
            onChange={(v) => setLocalValue(v)}
            autoFocus
          />
        );
      case "textarea":
        return (
          <Input.TextArea
            size="large"
            className="custom-input"
            value={localValue ?? ""}
            onChange={(e) => setLocalValue(e.target.value)}
            rows={3}
            autoFocus
          />
        );
      case "select":
        return (
          <Select
            size="large"
            className="w-100 custom-input"
            value={localValue || undefined}
            onChange={(v) => setLocalValue(v)}
            options={options}
            loading={optionsLoading}
            allowClear
            onClear={() => setLocalValue("")}
          />
        );
      case "hybrid":
        return (
          <HybridInput
            label={fieldLabel}
            value={localValue ?? ""}
            onChange={(v) => setLocalValue(v)}
            options={options}
            loading={optionsLoading}
          />
        );
      case "switch":
        return (
          <Switch
            checked={!!localValue}
            onChange={(checked) => setLocalValue(checked)}
          />
        );
      default:
        return (
          <Input
            size="large"
            className="custom-input"
            value={localValue ?? ""}
            onChange={(e) => setLocalValue(e.target.value)}
          />
        );
    }
  };

  return (
    <Modal
      title={`Modifica ${fieldLabel}`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Salva"
      cancelText="Annulla"
      width={440}
      centered
      confirmLoading={saving}
      destroyOnClose
    >
      <div style={{ marginTop: 16 }}>
        {fieldType !== "hybrid" && (
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            {fieldLabel}
          </label>
        )}
        {renderInput()}
      </div>
    </Modal>
  );
}

// ─── EditableField ──────────────────────────────────────────────────────────
type EditableFieldProps = {
  label: string;
  value: any;
  emptyText?: string;
  canEdit: boolean;
  onEdit: () => void;
};

export function EditableField({
  label,
  value,
  emptyText = "Non inserito",
  canEdit,
  onEdit,
}: EditableFieldProps) {
  const hasValue = value !== undefined && value !== null && value !== "";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>{label}:</strong>{" "}
        <span style={{ color: hasValue ? "inherit" : "var(--app-gray-500)" }}>
          {hasValue ? String(value) : emptyText}
        </span>
      </div>
      {canEdit && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          style={{
            border: "none",
            background: "transparent",
            color: "#9c1419",
            cursor: "pointer",
            padding: "2px 5px",
            fontSize: 12,
            flexShrink: 0,
            opacity: 0.6,
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.6"; }}
          title={`Modifica ${label}`}
        >
          <FontAwesomeIcon icon={faPen} />
        </button>
      )}
    </div>
  );
}
